import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { User } from '@supabase/supabase-js'
import { supabase } from '@/integrations/supabase/client'
import type { TipoOperacao } from '@/config/verticais'

export interface Perfil {
  id: string
  loja_id: string
  nome: string | null
  papel: 'admin' | 'avaliador'
}

export interface Loja {
  id: string
  nome: string
  slug: string
  whatsapp: string | null
  plano: 'basico' | 'pro'
  ativo: boolean
  stripe_customer_id: string | null
  stripe_subscription_id: string | null
  stripe_subscription_status: string | null
  trial_ends_at: string | null
  cortesia: boolean
  tipo_operacao: TipoOperacao
  fotos_entrada_obrigatorias: boolean
}

interface AuthContextType {
  user: User | null
  perfil: Perfil | null
  loja: Loja | null
  loading: boolean
  trialAvaliacoesUsadas: number
  signIn: (email: string, senha: string) => Promise<{ error: string | null }>
  signOut: () => Promise<void>
  reload: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [perfil, setPerfil] = useState<Perfil | null>(null)
  const [loja, setLoja] = useState<Loja | null>(null)
  const [loading, setLoading] = useState(true)
  const [trialAvaliacoesUsadas, setTrialAvaliacoesUsadas] = useState(0)

  const clearBrokenSession = useCallback(async () => {
    try {
      await supabase.auth.signOut()
    } catch {
      // ignora erro ao limpar sessão local quebrada
    } finally {
      setUser(null)
      setPerfil(null)
      setLoja(null)
      setTrialAvaliacoesUsadas(0)
      setLoading(false)
    }
  }, [])

  const loadPerfilELoja = useCallback(async (userId: string) => {
    const { data: perfilData } = await supabase
      .from('perfis')
      .select('*')
      .eq('id', userId)
      .maybeSingle()

    if (!perfilData) {
      setLoading(false)
      return
    }

    setPerfil(perfilData as Perfil)

    const { data: lojaData } = await supabase
      .from('lojas')
      .select('*')
      .eq('id', perfilData.loja_id)
      .single()

    if (lojaData) setLoja(lojaData as unknown as Loja)

    // Conta total de avaliações para trial (apenas se sem assinatura Stripe)
    let usadas = 0
    if (lojaData && !(lojaData as any).stripe_subscription_id) {
      const { count } = await supabase
        .from('avaliacoes')
        .select('id', { count: 'exact', head: true })
        .eq('loja_id', lojaData.id)
      usadas = count ?? 0
    }
    setTrialAvaliacoesUsadas(usadas)

    setLoading(false)
  }, [])

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      if (error?.message?.toLowerCase().includes('refresh token not found')) {
        await clearBrokenSession()
        return
      }

      setUser(session?.user ?? null)
      if (session?.user) loadPerfilELoja(session.user.id)
      else setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) loadPerfilELoja(session.user.id)
      else {
        setPerfil(null)
        setLoja(null)
        setTrialAvaliacoesUsadas(0)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [clearBrokenSession, loadPerfilELoja])

  async function signIn(email: string, senha: string) {
    const { error } = await supabase.auth.signInWithPassword({ email, password: senha })
    return { error: error ? 'E-mail ou senha inválidos.' : null }
  }

  async function signOut() {
    await supabase.auth.signOut()
    setUser(null)
    setPerfil(null)
    setLoja(null)
    setTrialAvaliacoesUsadas(0)
  }

  const reload = useCallback(async () => {
    const { data: { session }, error } = await supabase.auth.getSession()
    if (error?.message?.toLowerCase().includes('refresh token not found')) {
      await clearBrokenSession()
      return
    }
    if (session?.user) await loadPerfilELoja(session.user.id)
  }, [clearBrokenSession, loadPerfilELoja])

  return (
    <AuthContext.Provider value={{ user, perfil, loja, loading, trialAvaliacoesUsadas, signIn, signOut, reload }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
